import React, { Component } from 'react'
// @ts-ignore
import scrollToEl from 'scroll-to-element'

import { gtag_report_conversion } from '@utilities/analytics'
import { formatCurrency, formatPercentage } from '@utilities/numbers'
import { IInquiry } from '@features/inquiries'
import { emailInquiryAnswer } from '@features/leads/api'

import ContactForm from '@components/ContactForm'
import { EllipsisLoader } from '@components/Loaders'

import styles from './Answer.module.scss'

interface IProps {
  inquiry: IInquiry
}

interface IState {
  /** True is email is currently being sent */
  sendingEmail: boolean
  /** True if email from Contact form has been sent */
  emailed: boolean
  /** True if the ContactForm api returned errors */
  contactFormHasErrors: boolean
}

const initialContactFormState = {
  email: {
    name: 'email',
    focussed: false,
    touched: false,
    value: null,
    error: null
  },
  name: {
    name: 'name',
    focussed: false,
    touched: false,
    value: null,
    error: null
  }
}

export default class Answer extends Component<IProps, IState> {
  containerEl: any = null
  pieChartEl: any = null

  constructor(props: any) {
    super(props)
    this.state = {
      sendingEmail: false,
      emailed: false,
      contactFormHasErrors: false
    }
  }

  componentDidMount() {
    // Scroll window to the Answer
    scrollToEl(this.containerEl, {
      offset: -30,
      ease: 'out-quart',
      duration: 800
    })
  }

  scrollToTop(ev: any) {
    ev.preventDefault()
    scrollToEl(document.body, {
      offset: 0,
      ease: 'out-quart',
      duration: 800
    })
  }

  submitContactForm = (fieldValues: any) => {
    // Google Analytics and Adwords
    gtag_report_conversion('email_quote', 'cTIoCOffnZwBEI3lzOMC')

    this.setState({
      sendingEmail: true,
      emailed: false
    })
    emailInquiryAnswer({
      email: fieldValues.email,
      name: fieldValues.name
    })
      .then(() => {
        this.setState({
          sendingEmail: false,
          emailed: true,
          contactFormHasErrors: false
        })
      })
      .catch(() => {
        this.setState({
          sendingEmail: false,
          contactFormHasErrors: true
        })
      })
  }

  validateContactForm(fieldValues: any) {
    const errors: any = {}
    const requiredFields = ['email', 'name']
    for (const fieldName of requiredFields) {
      if (!fieldValues[fieldName]) {
        errors[fieldName] = 'This field is required.'
      }
    }
    if (Object.values(errors).length > 0) {
      return errors
    }
    return null
  }

  render() {
    let { inquiry } = this.props
    let { emailed, contactFormHasErrors } = this.state
    let { answer } = inquiry

    if (!inquiry.primary_applicant || !answer) {
      return null
    }

    let deposit = inquiry.deposit || 0
    let minHousePrice = 250000

    let STATUS = 0
    if (inquiry.property_purchase_price) {
      if (answer.can_afford_property === true) {
        STATUS = 1
      } else if (answer.can_make_loan === true && answer.property_value >= minHousePrice) {
        STATUS = 2
      }
    } else {
      if (answer.can_make_loan === true && answer.property_value >= minHousePrice) {
        STATUS = 1
      }
    }

    return (
      <section
        ref={(el) => (this.containerEl = el)}
        className="MgxInqForm-Answer Section -mod-spacing-top -padded -light-grey"
        data-status={STATUS}
      >
        <div className="Wrapper -wide">
          <ul className="List -divided">
            <li>
              <div className="Text -heading-left">
                <h3 className="Heading -three">Your Situation</h3>

                <p>
                  {answer.property_line_text} {answer.annual_income_line_text}{' '}
                  {answer.dependants_line_text} {answer.monthly_loan_outgoings_line_text}
                </p>

                <div className="Row -spaced -mod-margin-top">
                  <a href="/tenants/agreement-in-principle/" className="Button -large -primary">
                    Apply now
                  </a>

                  <button onClick={this.scrollToTop} className="Button -large -primary -inverted">
                    Update
                  </button>
                </div>
              </div>
            </li>

            <li>
              <div className="Text -heading-left">
                <h3 className="Heading -three">Your Quote</h3>

                <h4 className="Heading -five">{answer.quote_text}</h4>

                {answer.can_make_loan &&
                answer.property_value >= minHousePrice &&
                deposit <= 0.2 * answer.property_value ? (
                  <div>
                    <div className="Chart">
                      <ul className="Grid -two -mod-margin-top-2">
                        <li className="-mod-margin-bottom">
                          <h5 className="Heading -five">Your proposed investment:</h5>
                        </li>

                        <li className="-mod-margin-bottom-2">
                          <p>
                            <strong>
                              {formatCurrency(deposit, 0)} 
                            </strong>
                              &nbsp;({formatPercentage(answer.buyer_equity, 1)})
                          </p>
                          <em>
                            <p>
                              What you will invest, made as a
                              shared-equity loan to the purchasing company. You will not have to pay anything else.
                            </p>
                            <p>  
                              The total purchase price including stamp duty, fees and costs is <strong>
                              {formatCurrency(answer.total_purchase_price, 0)}</strong>.
                            </p>  
                          </em>
                        </li>
                        <li className="-mod-margin-bottom">
                          <h5 className="Heading -five">Your investment share:</h5>
                        </li>

                        <li className="-mod-margin-bottom-2">
                          <p>
                            <strong>{formatPercentage(answer.buyer_upside, 1)}</strong>
                          </p>
                          <p>
                            <em>
                              Proportion of the price increase you will receive, if the value of the home exceeds  {formatCurrency(answer.total_purchase_price, 0)}.
                            </em>
                          </p>
                        </li>

                        <li className="-mod-margin-bottom">
                          <h5 className="Heading -five">Your monthly rent:</h5>
                        </li>

                        <li className="-mod-margin-bottom-2">
                          <p>
                            <strong>{formatCurrency(answer.monthly_payments, 0)}</strong>
                          </p>
                          <em>
                            <p>
                              Your starting monthly payment. 
                            </p>
                            <p>  
                              Illustrative only; your actual monthly rent may vary depending on the location of the home.
                            </p>
                          </em>
                        </li>
                      </ul>
                    </div>

                    <div className="Box -padded -mod-margin-top-3 -mod-margin-bottom-3">
                      <div className="-mod-wrap">
                        <h4 className="Heading -four -mod-margin-bottom">
                          Email this result to yourself.
                        </h4>

                        <div
                          className={`${styles.contact_form_container}${
                            emailed ? ' is-closed' : ''
                          }`}
                        >
                          {contactFormHasErrors ? (
                            <div className="Row -mod-margin-bottom-2">
                              <div className="Notice -has-icon">
                                <i className="fas fa-exclamation-triangle" />
                                <p>
                                  Sorry, there was a problem.
                                  <br />
                                  Please contact customer.care@onstep.co to resolve the issue.
                                </p>
                              </div>
                            </div>
                          ) : null}

                          <div className={styles.form}>
                            <ContactForm
                              initialState={initialContactFormState}
                              validate={this.validateContactForm}
                              onSubmit={this.submitContactForm}
                            >
                              <div className="Notice">
                                <p>
                                  We may contact you to understand more about your interest in an
                                  OnStep home.
                                </p>
                              </div>

                              {emailed === false ? (
                                <div className="Row -spaced -mod-margin-top">
                                  <button
                                    className={`Button ${(this.state.sendingEmail &&
                                      '-secondary') ||
                                      '-primary'}`}
                                  >
                                    {this.state.sendingEmail ? (
                                      <EllipsisLoader color="white" />
                                    ) : (
                                      'Email me this quote'
                                    )}
                                  </button>
                                </div>
                              ) : null}
                            </ContactForm>
                          </div>
                          <span
                            className={`${styles.contact_form_confirmation} -mod-text-green -mod-margin-left`}
                          >
                            <i className="fas fa-check-circle" /> The quote has been emailed to you.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            </li>

            {answer.can_make_loan &&
            answer.property_value >= minHousePrice &&
            deposit <= 0.2 * answer.property_value ? (
              <li>
                <div className="Text -heading-left">
                  <h3 className="Heading -three">Key to Note</h3>
                  <p>
                    The return on your shared-equity loan investment will depend on the value of the
                    property at the time you exit. If the property value decreases you may lose all
                    your money.
                  </p>
                  <p>
                    See{' '}
                    <a
                      href="/how-it-works/#repayments"
                      className="Link"
                      target="_blank"
                      rel="noopener"
                    >
                      example investment returns
                    </a>
                    .
                  </p>
                  <p>
                    The rent on the OnStep home increases every year by an inflation measure called
                    CPIH (currently {formatPercentage(answer.applicable_inflation_rate, 2)}). This
                    will result in an increase in the monthly rent every year.
                  </p>
                </div>
              </li>
            ) : null}
          </ul>
        </div>
      </section>
    )
  }
}
